// client/src/components/forms/PublicFormPage.jsx
//
// /f/:tenantSlug/:formId        hosted form page (shareable link)
// /embed/f/:tenantSlug/:formId  the same form with no page chrome, for the
//                               iframe snippet (server.js lifts frame-ancestors
//                               for /embed/ only)
// No auth. Growth Phase 4, feature_key "forms".

import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { fetchPublicForm } from '../../services/formsApi';
import { assetUrl } from '../../utils/assetUrl';
import PublicFormRenderer from './PublicFormRenderer';
import './Forms.css';

export default function PublicFormPage({ embedded = false }) {
  const { t } = useTranslation();
  const { tenantSlug, formId } = useParams();
  const [form, setForm] = useState(null);
  const [state, setState] = useState('loading');

  // A public page must not wear the app theme (see PublicEventPage).
  useEffect(() => {
    const html = document.documentElement;
    const removed = [...html.classList].filter((c) => c.startsWith('theme-'));
    removed.forEach((c) => html.classList.remove(c));
    return () => removed.forEach((c) => html.classList.add(c));
  }, []);

  useEffect(() => {
    let alive = true;
    setState('loading');
    fetchPublicForm(tenantSlug, formId)
      .then((res) => {
        if (!alive) return;
        setForm(res.data);
        setState('ready');
        if (!embedded && res.data?.title) document.title = res.data.title;
      })
      .catch((err) => { if (alive) setState(err.status === 404 ? 'not-found' : 'error'); });
    return () => { alive = false; };
  }, [tenantSlug, formId, embedded]);

  return (
    <div className={`fm-page${embedded ? ' embed' : ''}`}>
      <div className="fm-page-inner">
        {!embedded && form?.tenant && (
          <div className="fm-page-head">
            {form.tenant.logo && <img src={assetUrl(form.tenant.logo)} alt="" />}
            <span>{form.tenant.name}</span>
          </div>
        )}
        {state === 'loading' && <div className="fm-page-status">{t('forms.public.loading', 'Loading…')}</div>}
        {state === 'not-found' && (
          <div className="fm-page-status">{t('forms.public.notFound', 'This form is closed or the link is wrong.')}</div>
        )}
        {state === 'error' && (
          <div className="fm-page-status">{t('forms.public.loadError', 'Something went wrong loading this form.')}</div>
        )}
        {state === 'ready' && (
          <>
            {!embedded && form.event && (
              <div className="fm-page-status">
                {form.event.slug
                  ? <a className="fm-link" href={`/ev/${encodeURIComponent(form.tenant.slug)}/${encodeURIComponent(form.event.slug)}`}>{form.event.name}</a>
                  : form.event.name}
              </div>
            )}
            <PublicFormRenderer form={form} tenantSlug={tenantSlug} embedded={embedded} />
          </>
        )}
        {!embedded && <p className="fm-page-foot">{t('forms.public.poweredBy', 'Powered by On-Screen')}</p>}
      </div>
    </div>
  );
}
